import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  QrCode, Download, Printer, Smartphone, 
  GraduationCap, Users, Briefcase, Bus,
  Settings, Copy, CheckCircle, BarChart3,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PWAManagementPage = () => {
  const { token, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [qrCodes, setQrCodes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [installStats, setInstallStats] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [qrRes, settingsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/pwa/qr-codes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/pwa/settings`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/pwa/install-stats`, { headers: getAuthHeader() })
      ]);
      
      setQrCodes(qrRes.data.qr_codes || []);
      setSettings(settingsRes.data);
      setInstallStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch PWA data:', error);
      toast.error('Failed to load PWA settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/pwa/settings`, settings, { 
        headers: getAuthHeader() 
      });
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const downloadQR = (qrCode) => {
    const link = document.createElement('a');
    link.href = qrCode.qr_code;
    link.download = `${qrCode.app_type}_app_qr.png`;
    link.click();
  };

  const copyInstallLink = (qrCode) => {
    const fullUrl = `${window.location.origin}${qrCode.install_url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copied to clipboard');
  };

  const printAllQRCodes = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>App QR Codes - Print</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .qr-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 30px; }
            .qr-card { text-align: center; border: 2px solid #e5e7eb; border-radius: 12px; padding: 20px; }
            .qr-card img { width: 200px; height: 200px; }
            .qr-card h3 { margin: 10px 0 5px; font-size: 18px; }
            .qr-card p { color: #6b7280; font-size: 14px; margin: 0; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { margin-bottom: 5px; }
            .header p { color: #6b7280; }
            @media print {
              .qr-card { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${settings?.school_name || 'School'} - App Installation</h1>
            <p>Scan QR code with your phone camera to install</p>
          </div>
          <div class="qr-grid">
            ${qrCodes.map(qr => `
              <div class="qr-card">
                <img src="${qr.qr_code}" alt="${qr.app_name} QR Code" />
                <h3>${qr.app_name}</h3>
                <p>For ${qr.app_type}s</p>
              </div>
            `).join('')}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const appIcons = {
    student: GraduationCap,
    parent: Users,
    teacher: Briefcase,
    driver: Bus
  };

  const appColors = {
    student: 'bg-blue-500',
    parent: 'bg-orange-500',
    teacher: 'bg-green-500',
    driver: 'bg-indigo-500'
  };

  if (loading) {
    return (
      <DashboardLayout title="App Management">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Mobile App Management">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Installs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{installStats?.total || 0}</div>
            </CardContent>
          </Card>
          
          {['student', 'parent', 'teacher', 'driver'].map(type => {
            const Icon = appIcons[type];
            return (
              <Card key={type}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{installStats?.by_app?.[type] || 0}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Tabs defaultValue="qrcodes">
          <TabsList>
            <TabsTrigger value="qrcodes" className="flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              QR Codes
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              App Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="qrcodes" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <QrCode className="w-5 h-5" />
                      App Installation QR Codes
                    </CardTitle>
                    <CardDescription>
                      Print or share these QR codes for easy app installation
                    </CardDescription>
                  </div>
                  <Button onClick={printAllQRCodes}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {qrCodes.map((qr) => {
                    const Icon = appIcons[qr.app_type];
                    return (
                      <Card key={qr.app_type} className="overflow-hidden">
                        <div className={`${appColors[qr.app_type]} p-4 text-white`}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-5 h-5" />
                            <span className="font-semibold">{qr.app_name}</span>
                          </div>
                        </div>
                        <CardContent className="p-4">
                          <div className="bg-white p-4 rounded-lg border mb-4">
                            <img 
                              src={qr.qr_code} 
                              alt={`${qr.app_name} QR Code`}
                              className="w-full"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => downloadQR(qr)}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={() => copyInstallLink(qr)}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Link
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Instructions */}
                <div className="mt-8 p-6 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-4">How to use QR Codes</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                        1
                      </div>
                      <div>
                        <p className="font-medium">Print QR Codes</p>
                        <p className="text-sm text-muted-foreground">Click "Print All" to print posters for notice boards</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                        2
                      </div>
                      <div>
                        <p className="font-medium">Scan with Phone</p>
                        <p className="text-sm text-muted-foreground">Users scan QR with phone camera</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">
                        3
                      </div>
                      <div>
                        <p className="font-medium">Install App</p>
                        <p className="text-sm text-muted-foreground">Follow on-screen instructions to install</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  App Branding Settings
                </CardTitle>
                <CardDescription>
                  Customize app names and colors for your school
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Student App Name</Label>
                    <Input 
                      value={settings?.student_app_name || ''} 
                      onChange={(e) => setSettings({...settings, student_app_name: e.target.value})}
                      placeholder="e.g., DPS Student"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Parent App Name</Label>
                    <Input 
                      value={settings?.parent_app_name || ''} 
                      onChange={(e) => setSettings({...settings, parent_app_name: e.target.value})}
                      placeholder="e.g., DPS Parent"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Teacher App Name</Label>
                    <Input 
                      value={settings?.teacher_app_name || ''} 
                      onChange={(e) => setSettings({...settings, teacher_app_name: e.target.value})}
                      placeholder="e.g., DPS Teacher"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Driver App Name</Label>
                    <Input 
                      value={settings?.driver_app_name || ''} 
                      onChange={(e) => setSettings({...settings, driver_app_name: e.target.value})}
                      placeholder="e.g., DPS Driver"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Theme Color</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="color"
                        value={settings?.theme_color || '#3b82f6'} 
                        onChange={(e) => setSettings({...settings, theme_color: e.target.value})}
                        className="w-20 h-10 p-1"
                      />
                      <Input 
                        value={settings?.theme_color || '#3b82f6'} 
                        onChange={(e) => setSettings({...settings, theme_color: e.target.value})}
                        placeholder="#3b82f6"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Background Color</Label>
                    <div className="flex gap-2">
                      <Input 
                        type="color"
                        value={settings?.background_color || '#ffffff'} 
                        onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                        className="w-20 h-10 p-1"
                      />
                      <Input 
                        value={settings?.background_color || '#ffffff'} 
                        onChange={(e) => setSettings({...settings, background_color: e.target.value})}
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Save Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PWAManagementPage;
